import { LandingComponentProps, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'
import productVialSemaglutide from '@/assets/kova/landing/product-vial-semaglutide.png'
import productVialTirzepatide from '@/assets/kova/landing/product-vial-tirzepatide.png'
import promoShot from '@/assets/kova/landing/promo-hero-universal.png'
import promoShotDesktop from '@/assets/kova/landing/promo-hero-universal-desktop.png'

// Promo creative: mobile Figma 6009:51451, desktop page 6009:49505. LandingKovaHeroPromo
// carries both breakpoints, so there is no LandingKovaHero. The blocks below the fold are the
// standard ones with the creative's own CTA copy; the arrow next to every label comes from
// styles/kova-landing.scss.
const schema: LandingScreenSchema = {
	config: { background: 'page' },

	order: [
		'LandingKovaHeader',
		'LandingKovaHeroPromo',
		'LandingKovaCalculator',
		'LandingKovaResults',
		'LandingKovaSteps',
		'LandingKovaReviews',
		'LandingKovaProduct',
		'LandingKovaFaq',
		'LandingKovaCta',
		'LandingKovaFooter',
	],

	components: {
		LandingKovaHeader: (): LandingComponentProps.KovaHeader => ({
			ctaLabel: 'Check my eligibility',
			rating: { caption: 'Over 200,000+ happy customers', label: 'Excellent!', score: '4.8' },
			ribbon: 'Lifetime Price Lock: Same price forever!',
		}),

		LandingKovaHeroPromo: (): LandingComponentProps.KovaHeroPromo => ({
			ctaLabel: 'Check my eligibility',
			description: 'Lose up to 20% of body weight with doctor-prescribed medication, with free delivery to your door.',
			features: [
				'Same price, every dose',
				'No in-person clinic visit',
				'Prescription & telemed visits included',
				'No insurance required',
				'Free shipping',
			],
			price: { label: 'Starting at', now: '$129', period: 'per month', was: '$299' },
			product: {
				badge: 'In-Stock',
				// 160x320 crop at x 215 of the 390px frame, so 15px short of the right edge.
				mobile: { image: promoShot, offsetRight: 15, offsetTop: 112, width: 160 },
				// 628x595 at (92, -30) of the 674x720 card — the phone rises above its top.
				desktop: { image: promoShotDesktop, left: 13.65, top: -4.17, width: 93.18 },
			},
			promises: [
				// Capitalised as on the assembled page (6009:47889) and in every other
				// block; the isolated hero frame writes it lower-case.
				{ icon: 'cross', label: 'No Hidden Fees' },
				{ icon: 'check', label: 'Takes ~2 minutes' },
			],
			rating: { caption: 'Over 200,000+ happy customers', label: 'Excellent!', score: '4.8' },
			title: 'Get GLP-1 treatment without clinic visits or insurance',
			trust: [
				{ icon: 'legitscript', label: 'LegitScript certified' },
				{ icon: 'clinicians', label: 'US-licensed clinicians' },
				{ icon: 'hipaa', label: 'HIPAA aligned' },
				{ icon: 'eligibility', label: 'Eligibility before charge' },
			],
		}),

		LandingKovaCalculator: (): LandingComponentProps.KovaCalculator => ({
			calculator: {
				ctaLabel: 'Check my eligibility',
				promises: [
					{ icon: 'cross', label: 'No Hidden Fees' },
					{ icon: 'check', label: 'Cancel Anytime' },
				],
				resultLabel: 'You could easily lose:',
				title: "Let's see how much weight can you shed by this fall?",
				unit: 'lbs',
				weight: { default: 220, lossRatio: 0.2, max: 400, min: 120, step: 1 },
				weightLabel: 'Your current weight',
			},
			perks: ['US-Certified Pharmacy', 'Personalized Treatments', '1:1 Medical Support'],
			stats: { label: 'happy customers', value: '200,000+' },
			titleParts: [
				{ text: 'Finally Lose Weight ' },
				{ accent: true, text: 'Without the Hunger' },
				{ text: ' or Hard Dieting' },
			],
		}),

		LandingKovaResults: (): LandingComponentProps.KovaResults => ({
			caption: 'See our customers’ results:',
			customers: ['sarah', 'james', 'rachel', 'chris', 'brian'],
			heading: 'We’ll help turn down hunger signals and feel full faster.',
			text: 'GLP-1 treatment can support your weight-loss goals when traditional methods aren’t enough. With Kova’s dedicated nursing team, you’re never left guessing or going it alone.',
		}),

		LandingKovaSteps: (): LandingComponentProps.KovaSteps => ({
			ctaLabel: 'Check my eligibility',
			disclaimer: 'Illustration only. Actual vial and packaging may vary by dispensing pharmacy.',
			steps: [
				{ title: 'Get $360+ Off Today!' },
				{ title: 'Provider writes an Rx' },
				{ title: 'Your order ships from our licensed US pharmacies' },
				{ title: 'Get your medication' },
				{ title: 'Begin treatment' },
			],
			subtitle: 'Direct healthcare, without the long wait times or doctor denials.',
			titleParts: [{ text: 'Get GLP-1 treatment ' }, { accent: true, text: 'without a clinic visit' }],
		}),

		LandingKovaReviews: (): LandingComponentProps.KovaReviews => ({
			reviews: [
				{
					author: 'Sarah lost 90lbs with Kova',
					quote:
						'“Lost 90 pounds with Kova and the difference is huge. Less brain fog, more energy, and I finally feel like myself again!”',
				},
				{
					author: 'James lost 48lbs with Kova',
					quote:
						'“Losing the weight and seeing fuller hair has been such a confidence boost. I’m 48 pounds down with Kova and didn’t expect to look this good.”',
				},
				{
					author: 'Rachel lost 51lbs with Kova',
					quote:
						'“Ate normal, but the weight kept creeping up and I was drained by 3pm. Kova finally reset something. 51 lbs later I feel like me again.”',
				},
				{
					author: 'Chris lost 51lbs with Kova',
					quote:
						'“I wasn’t eating that badly, but the weight kept creeping up. By 3pm I was wiped out, sleeping poorly, and struggling to focus. Kova helped turn things around.”',
				},
				{
					author: 'Brian lost 42lbs with Kova',
					quote:
						'“Nothing seemed to work for me anymore. With Kova, the weight finally started coming off, and I have so much more energy throughout the day. 42 lbs down and still going.”',
				},
				{
					author: 'Lisa lost 37lbs with Kova',
					quote:
						'“After years of starting over, I finally found something that worked for me. I’m 37 lbs down with Kova, and getting dressed in the morning feels completely different now.”',
				},
			],
			title: '200k+ Happy Customers :)',
		}),

		LandingKovaProduct: (): LandingComponentProps.KovaProduct => ({
			ctaLabel: 'Check my eligibility',
			// The creative brings the "Everything you need" card back to the universal
			// page (6009:47889) — the plain universal landing drops it.
			included: [
				'Medication, supplies, and instructions',
				'24/7 medical support',
				'One-on-one access to our team of clinicians',
				'Unlimited access to our Kova Med team',
				'Our promise to help you as much (or as little) as you want along the way',
				'Kova companion app included, iOS and Android',
			],
			includedTitle: '+ Everything you need',
			products: [
				{
					cadence: 'Only 1 injection per week',
					flag: { icon: 'lightning', label: 'More Affordable', tone: 'mint' },
					image: productVialSemaglutide,
					name: 'Compounded Semaglutide',
					note: 'Proven, effective, more affordable.',
				},
				{
					cadence: 'Only 1 injection per week',
					flag: { icon: 'clock', label: 'Fastest Results', tone: 'purple' },
					image: productVialTirzepatide,
					name: 'Compounded Tirzepatide',
					note: 'Faster results, dual-action, more expensive.',
				},
			],
			promises: [
				{ icon: 'cross', label: 'No Hidden Fees' },
				{ icon: 'check', label: 'Cancel Anytime' },
			],
			titleParts: [{ text: 'Medications ' }, { accent: true, text: 'In Stock ' }, { text: 'Ready to Ship' }],
		}),

		LandingKovaFaq: (): LandingComponentProps.KovaFaq => ({
			items: [
				{
					answer: [
						"If prescribed, your medication and supplies are shipped discreetly in temperature-controlled packaging. You'll receive tracking information once your order ships, and future shipments are timed around your medication schedule.",
					],
					question: 'How does shipping work?',
				},
				{
					answer: [
						'Compounded medications are generally not covered by insurance. You may be able to use HSA or FSA funds for eligible expenses, but eligibility varies by plan. Check with your HSA or FSA provider to confirm.',
					],
					question: 'Is this covered by insurance or HSA/FSA?',
				},
				{
					answer: [
						'Yes. Your plan renews automatically at the end of each plan period, but you can cancel anytime before your next renewal through your account. See the billing details below for more information.',
					],
					question: 'Can I cancel?',
				},
				{
					answer: [
						// TODO: neutral wording, not reviewed by legal.
						'GLP-1 medications can cause side effects, including nausea and digestive changes, especially when starting or changing doses. A licensed clinician will review your health information, discuss potential risks and side effects, and determine whether compounded GLP-1 treatment is appropriate for you.',
						"Compounded medications aren’t FDA-approved and haven’t been reviewed by the FDA for safety, effectiveness, or quality. Use the Kova app to track how you're feeling and share any concerns with your clinician.",
					],
					question: 'What should I know about safety and side effects?',
				},
				{
					answer: [
						'You pay at checkout. A licensed clinician reviews every order; if treatment isn’t approved — a full refund, automatically.',
					],
					question: 'How is eligibility determined?',
				},
				{
					answer: [],
					question: 'Billing & cancellation details',
					sections: [
						{
							text: 'Your plan renews at the end of each plan period (1, 3, 6 or 12 months) at the current price for that plan.',
							title: 'Your plan renews automatically',
						},
						{
							text: 'Cancel anytime before your renewal date through your account settings or by contacting support. Cancellation stops future charges. Once your medication has shipped, the current plan period is nonrefundable.',
							title: 'How to cancel',
						},
						{
							text: "If your plan price changes, we'll email you before your next renewal so you can review the new price or cancel before you're charged.",
							title: 'If the price changes',
						},
						{
							text: 'Your plan price includes your consultation, medication, supplies, shipping, and Kova app access. There are no separate mandatory membership or dispensing fees.',
							title: 'What your plan includes',
						},
					],
				},
			],
			subtitle: 'You have questions, we have answers.',
			title: 'We’ve got you.',
		}),

		LandingKovaCta: (): LandingComponentProps.KovaCta => ({
			badge: 'Get Started',
			ctaLabel: 'Check my eligibility',
			text: 'It’s not cheating, it’s science! Lose up to 20% of your body weight (1-2lbs per week) with medically supervised safe & effective treatment from the comfort of your home.',
			titleParts: [{ text: 'Ready to Stop Food Cravings?' }, { accent: true, text: ' Save $360+ Instantly!' }],
		}),

		LandingKovaFooter: (): LandingComponentProps.KovaFooter => ({
			contacts: { email: 'support@trykova.com', phone: '+1 831 480 7153' },
			// TODO: neutral wording, not reviewed by legal. The per-medication schemas
			// name their own compound here.
			disclaimer:
				'Compounded GLP-1 medications are not reviewed or approved by the FDA for safety, effectiveness or quality. They are prepared by a state- or FDA-registered compounding facility based on a prescription written for you by a licensed clinician. GLP-1 medications are prescription-only and appropriate only for eligible individuals. Individual results vary and are not guaranteed. This page is general information, not medical advice — talk to your clinician about whether treatment is right for you.',
			entity: [
				'LIFETECH APPS INC',
				'Registration number 10554303',
				'',
				'Registered Office:',
				'501 SILVERSIDE ROAD, SUITE 105',
				'WILMINGTON, NEW CASTLE,',
				'DELAWARE, ZIPCODE 19809',
			],
			links: [
				{ href: 'https://trykova.com/hipaa/', label: 'Notice of Privacy Practices' },
				{
					href: 'https://trykova.com/telehealth-medical-consent/',
					label: 'Consumer Health Data Privacy Notice',
				},
				{ href: 'https://trykova.com/terms/', label: 'Notice, Terms and Conditions' },
				{ href: 'https://trykova.com/privacy/', label: 'Privacy Notice' },
			],
			tagline:
				'Your daily support through GLP-1 treatment: medication, tracking, and clinician-aligned care in one place.',
		}),
	},
}

export default schema
